import React, { useState, useEffect, useRef } from "react";
import {
  Box, Typography, Paper, Stack, Chip, Checkbox, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Button, IconButton,
  useTheme, alpha, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LinkIcon from "@mui/icons-material/Link";

const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

const METHOD_COLORS = {
  GET:    "#0369a1",
  POST:   "#15803d",
  PUT:    "#7c3aed",
  PATCH:  "#c2410c",
  DELETE: "#be123c",
};

const toMap = (permission = []) => {
  const map = {};
  permission.forEach(({ path, method }) => {
    if (!map[path]) map[path] = {};
    map[path][method] = true;
  });
  return map;
};

const toArray = (map) => {
  const arr = [];
  Object.entries(map).forEach(([path, methods]) => {
    Object.entries(methods).forEach(([method, checked]) => {
      if (checked) arr.push({ path, method });
    });
  });
  return arr;
};

const PermissionEditor = ({ apiLinks = [], permission = [], onChange }) => {
  const theme = useTheme();
  const [selectedPath, setSelectedPath] = useState("");
  const [map, setMap] = useState(() => toMap(permission));

  // Track the "source" permission string to detect external changes (dialog open with new data)
  // vs internal changes (user toggling checkboxes)
  const permissionKey = useRef(JSON.stringify(permission));

  useEffect(() => {
    const newKey = JSON.stringify(permission);
    if (newKey !== permissionKey.current) {
      permissionKey.current = newKey;
      setMap(toMap(permission));
    }
  }, [permission]);

  const addedPaths = Object.keys(map).sort();
  const allPaths = [...new Set(apiLinks.map((l) => l.path))].sort();
  const availableToAdd = allPaths.filter((p) => !map[p]);
  const getMethodsForPath = (path) => (allPaths.includes(path) || map[path]) ? METHODS : [];

  const handleAdd = () => {
    if (!selectedPath) return;
    const available = getMethodsForPath(selectedPath);
    const newMap = { ...map, [selectedPath]: {} };
    if (available.includes("GET")) newMap[selectedPath]["GET"] = true;
    setMap(newMap);
    onChange(toArray(newMap));
    const rest = availableToAdd.filter((p) => p !== selectedPath);
    setSelectedPath(rest[0] || "");
  };

  const handleToggle = (path, method) => {
    const newMap = {
      ...map,
      [path]: { ...map[path], [method]: !map[path]?.[method] },
    };
    setMap(newMap);
    onChange(toArray(newMap));
  };

  const handleRemove = (path) => {
    const newMap = { ...map };
    delete newMap[path];
    setMap(newMap);
    onChange(toArray(newMap));
  };

  const totalPerms = toArray(map).length;

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={2} alignItems="center">
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>Add API path</InputLabel>
          <Select value={selectedPath} label="Add API path" onChange={(e) => setSelectedPath(e.target.value)}>
            {availableToAdd.map((p) => (
              <MenuItem key={p} value={p}>
                <Typography variant="caption" fontFamily="monospace">{p}</Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" color="secondary" startIcon={<AddIcon />}
          disabled={!selectedPath} onClick={handleAdd} sx={{ height: 40, flexShrink: 0 }}>
          Add
        </Button>
        <Chip label={`${totalPerms} permission${totalPerms !== 1 ? "s" : ""}`} size="small"
          color={totalPerms > 0 ? "secondary" : "default"} variant="outlined" sx={{ flexShrink: 0 }} />
      </Stack>

      {addedPaths.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center", borderStyle: "dashed" }}>
          <LinkIcon sx={{ fontSize: 36, mb: 1, opacity: 0.25 }} />
          <Typography variant="body2" color="text.secondary">No API paths added yet.</Typography>
          <Typography variant="caption" color="text.disabled">Select a path above and click Add.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.background.alt }}>
                <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>API PATH</TableCell>
                {METHODS.map((m) => (
                  <TableCell key={m} align="center" sx={{ px: 0.5, width: 64 }}>
                    <Chip label={m} size="small" sx={{
                      height: 20, fontSize: "0.65rem", fontWeight: 700,
                      bgcolor: METHOD_COLORS[m], color: "#fff", borderRadius: 0.75,
                    }} />
                  </TableCell>
                ))}
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {addedPaths.map((path, i) => {
                const available = getMethodsForPath(path);
                return (
                  <TableRow key={path} sx={{
                    "&:last-child td": { border: 0 },
                    backgroundColor: i % 2 === 0 ? "transparent" : alpha(theme.palette.secondary.main, 0.03),
                    "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.06) },
                  }}>
                    <TableCell>
                      <Typography variant="caption" fontFamily="monospace" color="text.secondary">{path}</Typography>
                    </TableCell>
                    {METHODS.map((m) => (
                      <TableCell key={m} align="center" sx={{ px: 0.5 }}>
                        {available.includes(m) ? (
                          <Checkbox
                            size="small"
                            checked={!!map[path]?.[m]}
                            onChange={() => handleToggle(path, m)}
                            sx={{
                              p: 0.25,
                              color: alpha(METHOD_COLORS[m], 0.35),
                              "&.Mui-checked": { color: METHOD_COLORS[m] },
                            }}
                          />
                        ) : (
                          <Box sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "divider" }} />
                          </Box>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ px: 0.5 }}>
                      <Tooltip title="Remove path">
                        <IconButton size="small" onClick={() => handleRemove(path)}
                          sx={{ "&:hover": { color: theme.palette.error.main } }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default PermissionEditor;
